import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {CopyButton} from 'src/components/copy-button';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {useEntitlementNamespaces} from 'src/hooks/dictionaries/useEntitlementNamespaces';
import {parseAndPrettyPrintJson} from 'src/modules/entitlements/utils/parseAndPrettyPrintJson';
import {isValidJsonString} from 'src/modules/manage-entitlements/utils/validateIsJson';
import {getResourcesByIds} from 'src/openapi/admin/apis/EntitlementsResourceApi';
import {useAppDispatch} from 'src/store/hooks';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {selectDrawerDetails, setResourceDetails} from '../entitlement-drawer.slice';

import {ResourceSkeleton} from './resource.skeleton';

import type {EntitlementResource} from 'src/openapi/admin/models/EntitlementResource';
import type {ServerResponse} from 'src/typedef';

type Props = {
    resourceId: string,
};

export const ResourceView = ({resourceId}: Props) => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();

    const {getNamespaceByCode} = useEntitlementNamespaces();
    const {createAbortSignal} = useAbortSignal();
    const {resourceDetails} = useSelector(selectDrawerDetails);

    useEffect(() => {
        const getResourceDetails = async () => {
            const signal = createAbortSignal();
            const response = await apiDispatch(getResourcesByIds({requestBody: [resourceId]}, {signal}));
            let serverResponse: ServerResponse<EntitlementResource> | null;

            if (response.payload.success) {
                const resource = response.payload.data[resourceId];

                serverResponse = response
                    ? {data: resource}
                    : {error: {status: 404, message: 'Resource not found'}};
            } else {
                serverResponse = {error: response.payload.data};
            }

            dispatch(setResourceDetails(serverResponse));
        };

        getResourceDetails();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Skeleton shown={!resourceDetails} view={<ResourceSkeleton/>}>
            <ServerResponseHandler serverError={resourceDetails?.error || null} smallView={true}>
                {resourceDetails?.data ? (
                    <Group direction="v" alignItems="start">
                        <div style={{width: 350}}>
                            <Label>Resource Description</Label>
                            {resourceDetails.data.description}
                        </div>
                        <div>
                            <Label>External ID</Label>
                            <Group alignItems="center">
                                <div style={{width: 300}}>
                                    <EllipsisTextHint>
                                        {resourceDetails.data.id}
                                    </EllipsisTextHint>
                                </div>
                                <CopyButton copiedText={resourceDetails.data.id} entityName="Resource External ID"/>
                            </Group>
                        </div>
                        <div>
                            <Label>Namespace</Label>
                            {getNamespaceByCode(resourceDetails.data.namespace)?.description}
                        </div>
                        {resourceDetails.data.metadata && isValidJsonString(resourceDetails.data.metadata) ? (
                            <div>
                                <Label>Metadata</Label>
                                <pre
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        overflowWrap: 'break-word',
                                        maxWidth: 425,
                                    }}>
                                    {parseAndPrettyPrintJson(resourceDetails.data.metadata)}
                                </pre>
                            </div>
                        ) : null}
                    </Group>
                ) : <div/>}
            </ServerResponseHandler>
        </Skeleton>
    );
};
